import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";

const NAV_LINKS = [
  { to: "/tradition", label: "Tradition" },
  { to: "/service", label: "Service" },
  { to: "/vespa-piaggio", label: "Vespa" },
  { to: "/pony-mofa", label: "Pony Mofa" },
  { to: "/zubehoer", label: "Zubehör" },
  { to: "/casa-markus", label: "Casa Markus" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-xl border-b ${
          scrolled
            ? "bg-[#F9F8F6]/95 border-[#E5E4E0] shadow-[0_1px_30px_-15px_rgba(0,0,0,0.18)]"
            : "bg-[#F9F8F6]/85 border-[#E5E4E0]/80"
        }`}
        data-testid="site-header"
      >
        <div className="container-wide flex items-center justify-between h-24 md:h-28">
          <Link to="/" className="flex items-center gap-3 md:gap-4 group shrink-0" data-testid="brand-logo">
            <img
              src="/logo-vespa.png"
              alt="Vespa Basel — Kauf + Service Garantie"
              width="150"
              height="80"
              className="block transition-transform duration-500 group-hover:scale-105"
              style={{ width: 150, height: 80, objectFit: "contain", objectPosition: "left center" }}
              data-testid="brand-logo-img"
            />
            <div className="hidden sm:flex flex-col leading-none border-l border-[var(--border)] pl-3 md:pl-4">
              <span className="font-serif text-xl md:text-2xl tracking-tightest text-[var(--anthracite)]">
                Vespa <span className="italic-accent text-[var(--sage-dark)]">Basel</span>
              </span>
              <span className="overline text-[9px] mt-1 text-[var(--muted-fg)]">
                Casa Markus Zürcher · 1949
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-9" data-testid="primary-nav">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `relative whitespace-nowrap text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive
                      ? "text-[var(--sage-dark)] after:content-[''] after:absolute after:left-0 after:-bottom-2 after:h-px after:w-full after:bg-[var(--sage-dark)]"
                      : "text-[var(--anthracite)] hover:text-[var(--sage-dark)]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <a
              href="tel:+41614218833"
              data-testid="header-call-button"
              className="hidden xl:inline-flex items-center gap-2 whitespace-nowrap text-[11px] uppercase tracking-[0.2em] text-[var(--anthracite)] hover:text-[var(--sage-dark)] transition-colors"
            >
              <Phone size={13} strokeWidth={1.5} />
              061 421 88 33
            </a>
            <Link
              to="/casa-markus"
              data-testid="header-contact-cta"
              className="hidden md:inline-flex btn-primary !py-3 !px-5 text-[10px]"
            >
              Termin
            </Link>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-11 h-11 grid place-items-center border border-[var(--anthracite)] text-[var(--anthracite)]"
              data-testid="mobile-menu-toggle"
              aria-label="Menü"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
        <div className="tricolore h-px w-full opacity-70" aria-hidden />
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden bg-[var(--cream)] pt-24"
            data-testid="mobile-menu"
          >
            <div className="container-wide flex flex-col gap-6 py-10">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <Link
                    to={link.to}
                    data-testid={`mobile-nav-${link.to.replace("/", "")}`}
                    className="font-serif text-4xl tracking-tightest text-[var(--anthracite)]"
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <div className="tricolore h-[3px] w-24 mt-6" />
              <a
                href="tel:+41614218833"
                className="overline text-[var(--sage-dark)] mt-4"
                data-testid="mobile-call-link"
              >
                +41 61 421 88 33
              </a>
              <a
                href="mailto:original@vespa-basel.ch"
                className="overline text-[var(--sage-dark)]"
                data-testid="mobile-email-link"
              >
                original@vespa-basel.ch
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
